import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  useToast
} from '@chakra-ui/react';
import Form from '@rjsf/chakra-ui';
import validator from "@rjsf/validator-ajv8";
import { reverseTransformData, transformOptions } from '../../util/helper';
import TypeaheadWidget from '../../CommonComponents/TypeaheadWidget';
import { getResearchCategoriesAPI } from '../../services/Films';
import { createNewQuestion, getQuestionData, updateQuestionData } from '../../services/Questions';
import { RjsfSubmitButton } from '../../RJSFCommonSchemas';
import FormLoader from '../../CommonComponents/FormLoader';
import { showErrorToast, showSuccessToast } from "../../util/errorHandler";



const schema = {
  "title": "Question Fields",
  "description": "",
  "type": "object",
  "properties": {
    "title": {
      "type": "string",
      "title": "Title",
    },
    "seq": {
      "type": "number",
      "title": "Sequence",
      "default": 0
    },
    "isVisible": {
      "type": "boolean",
      "title": "Visible ?",
      "default": true
    },
    "template_name": {
      "type": "string",
      "title": "Template Type",
      "default": ""
    },
    "options": {
      "type": "array",
      "title": "Options",
      "items": {
        "type": "string"
      }
    },
    "correctAnswer": {
      "type": "string",
      "title": "Correct Answer",
      "default": ""
    },
  },
  required: ["title"]
};

const uiSchema = (isSubmitting) => ({
  ...RjsfSubmitButton(isSubmitting),
  "ui:style": {
    fontSize: "14px", // Apply font size inline
  },
  title: {
    "ui:emptyValue": ""
  },
  isVisible: {
    "ui:widget": "checkbox",
    "ui:options": {
      "inline": true
    },
    "ui:emptyValue": ""
  },
  options: {
    "items": {
      "ui:emptyValue": ""
    }
  },
  template_name: {
    "ui:options": {
      "inputProps": {
        "autoComplete": "off"
      }
    },
    "ui:emptyValue": ""
  },
  correctAnswer: {
    "ui:emptyValue": "",
    "ui:options": {
      "inputProps": {
        "autoComplete": "off"
      }
    }
  }
});

const QuestionDetailForm = () => {

  const { alias, questionId } = useParams();
  const [processedData, setProcessedData] = useState({});
  const [quickSave, setQuickSave] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const toast = useToast();
  const navigate = useNavigate();

  const widgets = {
    typeahead: (props) => (
      <TypeaheadWidget {...props} fetchOptions={(inputValue) => fetchCategories(inputValue, "alias")} />
    ),
    typeahead1: (props) => (
      <TypeaheadWidget {...props} fetchOptions={(inputValue) => fetchCategories(inputValue, "value")} />
    ),
  };

  const formData = processedData;

  const fetchCategories = async (inputValue, keyForLabelValue = "alias") => {
    const result = await getResearchCategoriesAPI();
    const transformedOptions = transformOptions(result, keyForLabelValue);

    // This function return the options
    return transformedOptions.filter((option) =>
      option.label.toLowerCase().includes(inputValue.toLowerCase())
    );
  }

  // Fetch initial data
  useEffect(() => {
    const getBasicData = async () => {
      try {
        if (alias) {
          const result = await getQuestionData(alias, questionId);
          if (result) {
            // To Show image on form need to provide url to image field which is only used to show the image
            result.template_name = result?.template?.template_name;
            setProcessedData(result);
            setQuickSave(false);
          }
        }
      } catch (error) {
        console.error('Error fetching data:', error);
                showErrorToast(toast, "Failed to fetch data");
      } finally {
        setIsLoading(false);
      }
    };

    getBasicData();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alias, quickSave]);

  // Submit updated data
  const handleSubmit = async (formData) => {

    const fieldsToTransform = [
      'post_images_alias'
    ];

    const updatedFormData = reverseTransformData(formData, fieldsToTransform);

    try {
      if (alias && questionId && questionId !== "create") {
        setIsSubmitting(true);
        const result = await updateQuestionData(alias, questionId, updatedFormData);
        if (result?.status === 200) {
          setQuickSave(prev => !prev);

                    showSuccessToast(toast, "Data updated successfully");
        }
      }
      else {
        setIsSubmitting(true);
        const result = await createNewQuestion(alias, updatedFormData, "posts");  // Call the create API
        if (result?.status === 201) {
          setQuickSave(prev => !prev);
                    showSuccessToast(toast, "New question created successfully");
          const postId = result?.data?.question?._id
          navigate(`/posts/${alias}/quiz${postId ? "/" + postId : ""}`);  // Navigate to the newly created post
        }
      }
    } catch (error) {
      console.error('Something went wrong data:', error);
            showErrorToast(toast, "Failed to fulfill request");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box maxW="1200px">
      {isLoading ? (
        <FormLoader />
      ) : (
          <Form
            schema={schema}
            uiSchema={uiSchema(isSubmitting)}
            formData={formData}
            validator={validator}
            onSubmit={(e) => handleSubmit(e.formData)}
            widgets={widgets}
            fontsize={"14px"}
          />
      )}
    </Box>
  );
};

export default QuestionDetailForm;